import { getAuthToken, clearAuthToken } from '~/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Custom API error with status code
 */
export class ApiError extends Error {
  status: number;
  statusText: string;

  constructor(status: number, statusText: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isServerError() {
    return this.status >= 500;
  }

  get isNetworkError() {
    return this.status === 0;
  }

  get isSubscriptionRequired() {
    return this.status === 402;
  }
}

export interface Signal {
  id: string;
  position_id: string;
  message_id: number;
  action: 'BTO' | 'STC' | 'trim' | 'watch' | 'none';
  ticker: string;
  original_ticker: string | null;
  strike: number;
  option_type: 'C' | 'P';
  expiry: string | null;
  expiry_type: '0dte' | 'swing';
  signal_price: number;
  current_price: number | null;
  confidence: number;
  interpretation: string | null;
  status: 'active' | 'executed' | 'dismissed' | 'expired';
  freshness: 'fresh' | 'caution' | 'chasing' | 'discount';
  created_at: string;
  updated_at: string;
  // Identity: trader_id is canonical; trader_username = cached current_nick
  // (legacy alias kept for one deploy while dashboard is migrated).
  trader_id?: string;
  trader_username?: string;
  channel_name?: string;
  message_content?: string;
  // Thread summary for closed position badges
  thread_exit_price?: number | null;
  thread_exit_action?: 'STC' | 'trim' | null;
  // Edit tracking
  message_is_edited?: boolean;
  dismissed_reason?: string | null;
  replaced_by?: string | null;
}

export interface TradeThread {
  position_id: string;
  ticker: string;
  original_ticker: string | null;
  strike: number;
  option_type: 'C' | 'P';
  expiry: string | null;
  expiry_type: '0dte' | 'swing';
  entry_price: number | null;
  exit_price: number | null;
  current_price: number | null;
  pnl_percent: number | null;
  thread_status: 'active' | 'closed' | 'expired' | 'watching';
  position_status: 'watching' | 'open' | 'partial' | 'closed' | 'dismissed' | 'expired';
  signal_count: number;
  // Identity: trader_id keys URLs and ownership checks; trader_username is
  // the cached current_nick used for display.
  trader_id: string;
  trader_username: string;
  channel_name: string;
  created_at: string;
  updated_at: string;
  last_action_at: string | null;
  first_signal_id: string;
}

export interface Subscription {
  id: string;
  trader_username: string;
  trader_display_name: string | null;
  trader_avatar_url: string | null;
  channel_id: string;
  channel_name: string;
  created_at: string;
  channel_type: 'plays' | 'alerts';
  last_message_at: string | null;
  message_count: number;
  is_watching: boolean;
}

export interface ExtensionStatus {
  connected: boolean;
  channels: string[];
  lastSeen: number | null;
  extensionVersion?: string;
}

export interface Message {
  id: number;
  discord_message_id: string;
  subscription_id: string;
  author_username: string | null;
  content: string;
  timestamp: string;
  raw_payload: string;
  processing_status: string | null;
  created_at: string;
  channel_name: string | null;
}

export interface MessageStats {
  total_messages: number;
  unique_authors: number;
  channels_with_messages: number;
  last_message_at: string | null;
  messages_last_hour: number;
}

export interface ApiCall {
  id: number;
  message_id: number | null;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  prompt: string;
  response: string | null;
  error: string | null;
  duration_ms: number | null;
  created_at: string;
  /** When the message arrived (ingest). Used to show end-to-end pipeline latency. */
  message_created_at: string | null;
  message_content: string | null;
  author_username: string | null;
}

export interface PositionAtTime {
  id: string;
  ticker: string;
  strike: number;
  option_type: 'C' | 'P';
  expiry: string | null;
  expiry_type: '0dte' | 'swing' | null;
  status: 'watching' | 'open' | 'partial' | 'closed' | 'expired' | 'dismissed';
  remaining_pct: number;
  avg_entry_price: number | null;
  entry_time: string | null;
  entry_notes: string | null;
}

export interface SignalContext {
  signal: {
    id: string;
    action: string;
    ticker: string;
    strike: number;
    option_type: 'C' | 'P';
    signal_price: number;
    confidence: number;
    interpretation: string | null;
    freshness: string;
    created_at: string;
    position_id: string | null;
  };
  currentMessage: {
    content: string;
    author_username: string;
    timestamp: string;
  };
  leadUpMessages: Array<{ content: string; timestamp: string; author_username: string }>;
  positionsAtParseTime: PositionAtTime[];
  llmResponse: unknown;
  apiCall: {
    model: string;
    input_tokens: number | null;
    output_tokens: number | null;
    duration_ms: number | null;
    created_at: string;
  } | null;
}

export interface SignalEdit {
  id: number;
  signal_id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  edited_by: string;
  edited_at: string;
}

export interface Settings {
  currency: string;
  confidence_threshold: string;
}

export interface StockQuote {
  ticker: string;
  price: number;
  change: number;
  changePercent: number;
  previousClose: number;
  timestamp: number;
}

// Backend stamps every response with X-App-Version (the Cloudflare worker
// version UUID). The first response we see locks in the "boot" version;
// any later response with a different value means the user is running a
// stale JS bundle against a newer deploy. We force-reload once to pick up
// the new bundle. Guarded so we never reload twice in a single session.
let bootAppVersion: string | null = null;
let reloadingForVersion = false;

function checkAppVersion(res: Response): void {
  const v = res.headers.get('X-App-Version');
  if (!v) return;
  if (bootAppVersion === null) {
    bootAppVersion = v;
    return;
  }
  if (v !== bootAppVersion && !reloadingForVersion) {
    reloadingForVersion = true;
    void performVersionReload();
  }
}

async function performVersionReload(): Promise<void> {
  // Force the SW to fetch a fresh /sw.js (browsers otherwise only check it
  // every 24h on long-running PWAs) and skipWaiting so the new precache
  // takes over before we reload.
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update();
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    } catch {
      // SW unavailable — fall through to a plain reload.
    }
  }
  // Small delay so any in-flight request doesn't get cancelled mid-render.
  setTimeout(() => window.location.reload(), 250);
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  let res: Response;

  try {
    // Get auth token from localStorage
    const token = getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Merge with any passed headers
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include', // Include cookies for auth
      headers,
    });
    checkAppVersion(res);
  } catch (err) {
    // Network error (offline, CORS, etc.)
    throw new ApiError(0, 'Network Error', 'Unable to connect to server. Check your connection.');
  }

  if (!res.ok) {
    let message = res.statusText;
    try {
      const errorBody = await res.json();
      message = errorBody.error || errorBody.message || res.statusText;
    } catch {
      // Response wasn't JSON
    }

    // Expired/invalid token — clear and redirect to login
    if (res.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }

    throw new ApiError(res.status, res.statusText, message);
  }

  return res.json();
}

// Signals API
export const signalsApi = {
  list: (status = 'active') =>
    fetchApi<{ signals: Signal[] }>(`/api/signals?status=${status}`),

  get: (id: string) =>
    fetchApi<{ signal: Signal; threadHistory: Signal[] }>(`/api/signals/${id}`),

  dismiss: (id: string) =>
    fetchApi<{ success: boolean }>(`/api/signals/${id}/dismiss`, { method: 'POST' }),

  all: (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    action?: string;
    status?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.action) searchParams.set('action', params.action);
    if (params?.status) searchParams.set('status', params.status);
    return fetchApi<{ signals: Signal[]; total: number; limit: number; offset: number }>(
      `/api/signals/all?${searchParams}`
    );
  },

  traders: () =>
    fetchApi<{ traders: Array<{ trader_id: string; current_nick: string; trader_username: string }> }>(
      '/api/signals/traders',
    ),

  threads: (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: 'active' | 'closed' | 'expired' | 'watching' | '';
    trader?: string | string[];
    expiry_type?: '0dte' | 'swing' | '';
    /** Server-side filter: only plays from traders in user_followed_traders. */
    following?: boolean;
    /** Server-side filter: include the authed user's own plays. */
    me?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.trader) {
      const t = Array.isArray(params.trader) ? params.trader.join(',') : params.trader;
      if (t) searchParams.set('trader', t);
    }
    if (params?.expiry_type) searchParams.set('expiry_type', params.expiry_type);
    if (params?.following) searchParams.set('following', 'true');
    if (params?.me) searchParams.set('me', 'true');
    return fetchApi<{ threads: TradeThread[]; previewThreads?: TradeThread[]; total: number; limit: number; offset: number }>(
      `/api/signals/threads?${searchParams}`
    );
  },

  edit: (id: string, data: {
    ticker?: string;
    strike?: number;
    option_type?: 'C' | 'P';
    expiry?: string | null;
    expiry_type?: '0dte' | 'swing';
    signal_price?: number;
    status?: 'watching' | 'open' | 'partial' | 'closed' | 'dismissed' | 'expired';
  }) => fetchApi<{ success: boolean }>(`/api/signals/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  threadSignals: (threadId: string) =>
    fetchApi<{ thread: TradeThread | null; signals: Signal[]; edits: SignalEdit[] }>(`/api/signals/threads/${encodeURIComponent(threadId)}`),

  signalContext: (signalId: string) =>
    fetchApi<SignalContext>(`/api/signals/${encodeURIComponent(signalId)}/context`),

  deletePosition: (positionId: string) =>
    fetchApi<{ success: boolean }>(`/api/signals/positions/${encodeURIComponent(positionId)}`, {
      method: 'DELETE',
    }),
};

// Subscriptions API
export const subscriptionsApi = {
  list: () => fetchApi<{ subscriptions: Subscription[]; extension_connected: boolean }>('/api/subscriptions'),

  create: (data: {
    trader_username: string;
    trader_display_name?: string;
    channel_id: string;
    channel_name: string;
  }) =>
    fetchApi<{ success: boolean; id: string }>('/api/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/api/subscriptions/${id}`, { method: 'DELETE' }),

  updateType: (id: string, channel_type: 'plays' | 'alerts') =>
    fetchApi<{ success: boolean; id: string; channel_type: string }>(`/api/subscriptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ channel_type }),
    }),
};

export interface TraderMessage {
  id: number;
  discord_message_id: string;
  subscription_id: string;
  trader_id: string;
  server_id: string;
  author_username: string; // cached current_nick
  content: string;
  timestamp: string;
  raw_payload: string | null;
  processing_status: string | null;
  is_edited: number | null;
  created_at: string;
  channel_name: string | null;
  channel_id: string | null;
  channel_type: 'plays' | 'alerts' | null;
  reply_to_discord_message_id?: string | null;
  reply_to_author?: string | null;
  reply_to_content?: string | null;
}

export interface TraderProfile {
  trader_id: string;
  current_nick: string;
  username: string; // legacy alias for current_nick
  server_id: string;
  discord_user_id: string;
  last_seen_at: string;
  message_count: number;
  last_message_at: string | null;
  followed: boolean;
  notify_plays: boolean;
  notify_messages: boolean;
  hidden: boolean;
}

export interface TraderPosition {
  position_id: string;
  trader_id: string;
  trader_username: string;
  ticker: string;
  strike: number;
  option_type: 'C' | 'P';
  expiry: string | null;
  expiry_type: '0dte' | 'swing' | null;
  position_status: 'watching' | 'open' | 'partial' | 'closed' | 'dismissed' | 'expired';
  remaining_pct: number;
  entry_price: number | null;
  exit_price: number | null;
  realized_pnl: number | null;
  entry_time: string | null;
  exit_time: string | null;
  created_at: string;
  updated_at: string;
  signal_count: number;
  last_action_at: string | null;
}

export const tradersApi = {
  /** Most-followed traders across the user base, with the authed user's follow flag. */
  top: (limit = 3) =>
    fetchApi<{
      traders: Array<{
        trader_id: string;
        current_nick: string;
        trader_username: string; // legacy alias
        follower_count: number;
        followed: boolean;
      }>;
    }>(`/api/traders/top?limit=${limit}`),

  get: (traderId: string) =>
    fetchApi<TraderProfile>(`/api/traders/${encodeURIComponent(traderId)}`),

  messages: (traderId: string, limit = 100) =>
    fetchApi<{ trader_id: string; messages: TraderMessage[]; total: number; limit: number }>(
      `/api/traders/${encodeURIComponent(traderId)}/messages?limit=${limit}`,
    ),

  positions: (traderId: string, status: 'open' | 'closed' | 'all' = 'all') =>
    fetchApi<{ trader_id: string; open: TraderPosition[]; closed: TraderPosition[] }>(
      `/api/traders/${encodeURIComponent(traderId)}/positions?status=${status}`,
    ),

  follow: (traderId: string) =>
    fetchApi<{ success: boolean; trader_id: string; followed: true }>(
      `/api/traders/${encodeURIComponent(traderId)}/follow`,
      { method: 'POST' },
    ),

  unfollow: (traderId: string) =>
    fetchApi<{ success: boolean; trader_id: string; followed: false }>(
      `/api/traders/${encodeURIComponent(traderId)}/follow`,
      { method: 'DELETE' },
    ),

  // Implicitly creates the follow row if it doesn't exist; pass either flag.
  updateNotifications: (
    traderId: string,
    prefs: { notify_plays?: boolean; notify_messages?: boolean },
  ) =>
    fetchApi<{
      success: boolean;
      trader_id: string;
      followed: boolean;
      notify_plays: boolean;
      notify_messages: boolean;
    }>(`/api/traders/${encodeURIComponent(traderId)}/notifications`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    }),
};

export const alertsApi = {
  list: (params?: { limit?: number; since?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.since) q.set('since', params.since);
    const qs = q.toString();
    return fetchApi<{ messages: TraderMessage[]; total: number; limit: number }>(
      `/api/alerts${qs ? '?' + qs : ''}`,
    );
  },
};

// Settings API
export const settingsApi = {
  get: () => fetchApi<{ settings: Settings }>('/api/settings'),

  update: (settings: Partial<Settings>) =>
    fetchApi<{ success: boolean }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};

export interface PendingCount {
  pending: number;
  queued: number;
  rate_limited: number;
  processed: number;
  error: number;
  null: number;
  total_unprocessed: number;
}

export interface ProcessPendingResult {
  success: boolean;
  processed: number;
  signals_created: number;
  results: Array<{
    messageId: number;
    status: string;
    signalId?: string;
    error?: string;
  }>;
}

// Messages API (debug)
export const messagesApi = {
  list: (limit = 50, offset = 0) =>
    fetchApi<{ messages: Message[]; total: number; limit: number; offset: number }>(
      `/api/messages?limit=${limit}&offset=${offset}`
    ),

  stats: () => fetchApi<MessageStats>('/api/messages/stats'),

  apiCalls: (limit = 20) =>
    fetchApi<{ api_calls: ApiCall[] }>(`/api/messages/api-calls?limit=${limit}`),

  errorMessages: (limit = 50, offset = 0) =>
    fetchApi<{ messages: Message[]; total: number; limit: number; offset: number }>(
      `/api/messages?status=error,pending,queued,rate_limited&limit=${limit}&offset=${offset}`
    ),

  pendingCount: () => fetchApi<PendingCount>('/api/messages/pending-count'),

  processPending: (limit = 10) =>
    fetchApi<ProcessPendingResult>(`/api/messages/process-pending?limit=${limit}`, {
      method: 'POST',
    }),
};

// Prices API
export const pricesApi = {
  /**
   * Get current price for a single ticker
   */
  get: (ticker: string) => fetchApi<StockQuote>(`/api/prices/${encodeURIComponent(ticker)}`),

  /**
   * Get current prices for multiple tickers
   */
  batch: (tickers: string[]) =>
    fetchApi<{ quotes: Record<string, StockQuote>; requested: number; found: number }>(
      `/api/prices?tickers=${tickers.map(encodeURIComponent).join(',')}`
    ),

  /**
   * Live option premiums for a set of contracts (open positions). Returns a
   * map keyed by the `key` you pass per contract → { price, changePercent } | null.
   */
  options: (contracts: Array<{ key: string; ticker: string; strike: number; option_type: 'C' | 'P'; expiry: string | null }>) =>
    fetchApi<{ quotes: Record<string, { price: number; changePercent: number | null } | null> }>(
      `/api/prices/options`,
      { method: 'POST', body: JSON.stringify({ contracts }) },
    ),
};

// API Keys
export interface ApiKey {
  id: string;
  name: string;
  last_used_at: string | null;
  created_at: string;
}

export const keysApi = {
  list: () => fetchApi<{ keys: ApiKey[] }>('/api/keys'),

  create: (name: string) =>
    fetchApi<{ id: string; key: string; name: string; message: string }>('/api/keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/api/keys/${id}`, { method: 'DELETE' }),
};

// Guild access policies (admin)
export interface GuildAccessPolicy {
  guild_id: string;
  guild_name: string;
  required_role_ids: string;
  // Roles that grant free premium (paywall bypass). Empty = none.
  premium_bypass_role_ids: string;
  enabled: number;
  created_at: string;
  updated_at: string;
}

export const guildPoliciesApi = {
  list: () => fetchApi<{ policies: GuildAccessPolicy[] }>('/api/admin/guild-policies'),

  create: (data: { guild_id: string; guild_name: string; required_role_ids?: string; premium_bypass_role_ids?: string; enabled?: boolean }) =>
    fetchApi<{ success: boolean }>('/api/admin/guild-policies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (guildId: string, data: { guild_name?: string; required_role_ids?: string; premium_bypass_role_ids?: string; enabled?: boolean }) =>
    fetchApi<{ success: boolean }>(`/api/admin/guild-policies/${guildId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (guildId: string) =>
    fetchApi<{ success: boolean }>(`/api/admin/guild-policies/${guildId}`, { method: 'DELETE' }),
};

// Admin trader operations
export const adminTradersApi = {
  setHidden: (traderId: string, hidden: boolean) =>
    fetchApi<{ success: boolean; trader_id: string; hidden: boolean }>(
      `/api/admin/traders/${encodeURIComponent(traderId)}`,
      { method: 'PATCH', body: JSON.stringify({ hidden }) },
    ),
};

// Admin: manual guild grants
export interface AdminUserSearchHit {
  id: string;
  username: string;
  role: string;
  avatar: string | null;
  manual_grants?: number;
}

export interface AdminUserList {
  users: AdminUserSearchHit[];
  total: number;
  limit: number;
  offset: number;
}

export interface ManualGrant {
  guild_id: string;
  source: string;
  created_at: string;
  guild_name: string | null;
}

export const adminUsersApi = {
  search: (q: string) =>
    fetchApi<{ users: AdminUserSearchHit[] }>(
      `/api/admin/users?search=${encodeURIComponent(q)}`,
    ),

  list: (limit = 50, offset = 0) =>
    fetchApi<AdminUserList>(
      `/api/admin/users?limit=${limit}&offset=${offset}`,
    ),

  get: (userId: string) =>
    fetchApi<{ user: AdminUserSearchHit }>(
      `/api/admin/users/${encodeURIComponent(userId)}`,
    ),

  listGrants: (userId: string) =>
    fetchApi<{ grants: ManualGrant[] }>(
      `/api/admin/users/${encodeURIComponent(userId)}/grants`,
    ),

  addGrant: (userId: string, guildId: string) =>
    fetchApi<{ success: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/grants`,
      { method: 'POST', body: JSON.stringify({ guild_id: guildId }) },
    ),

  removeGrant: (userId: string, guildId: string) =>
    fetchApi<{ success: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(guildId)}`,
      { method: 'DELETE' },
    ),
};

// Billing API
export interface BillingStatus {
  hasSubscription: boolean;
  isActive: boolean;
  // Access granted by a Discord role rather than Stripe.
  bypass: boolean;
  bypassReason: 'discord_role' | null;
  subscription: {
    id: string;
    status: string;
    priceId: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export const billingApi = {
  status: () => fetchApi<BillingStatus>('/api/billing/status'),

  createCheckout: () =>
    fetchApi<{ url: string }>('/api/billing/checkout', { method: 'POST' }),

  createPortal: () =>
    fetchApi<{ url: string }>('/api/billing/portal', { method: 'POST' }),
};

// Leaderboard API
export interface LeaderboardTrader {
  rank: number;
  trader_id: string;
  trader_username: string;
  display_name: string;
  avatar_url: string | null;
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_return_pct: number;
  best_trade_pct: number | null;
  total_realized_pnl: number;
}

export const leaderboardApi = {
  get: (period: 'week' | 'month' | 'all' = 'all') =>
    fetchApi<{ traders: LeaderboardTrader[]; period: string }>(`/api/leaderboard?period=${period}`),
};

// Push / Trader Alerts API
export interface TraderAlert {
  trader_id: string;
  current_nick: string;
  trader_username: string; // legacy alias for current_nick
  trader_display_name: string | null;
  muted: boolean;
  followed: boolean;
  notify_plays: boolean;
  notify_messages: boolean;
}

export const pushApi = {
  traders: (params?: { limit?: number; offset?: number; search?: string; sort?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sort) searchParams.set('sort', params.sort);
    return fetchApi<{ traders: TraderAlert[]; total: number; limit: number; offset: number }>(
      `/api/push/traders?${searchParams}`
    );
  },

  muteTrader: (traderId: string) =>
    fetchApi<{ success: boolean }>(`/api/push/traders/${encodeURIComponent(traderId)}/mute`, {
      method: 'PUT',
    }),

  unmuteTrader: (traderId: string) =>
    fetchApi<{ success: boolean }>(`/api/push/traders/${encodeURIComponent(traderId)}/mute`, {
      method: 'DELETE',
    }),

  muteAll: () =>
    fetchApi<{ success: boolean }>('/api/push/traders/mute/all', { method: 'PUT' }),

  unmuteAll: () =>
    fetchApi<{ success: boolean }>('/api/push/traders/mute/all', { method: 'DELETE' }),

  getSchedule: () =>
    fetchApi<{ alertSchedule: 'market_hours' | 'all_time' }>('/api/push/schedule'),

  setSchedule: (alertSchedule: 'market_hours' | 'all_time') =>
    fetchApi<{ success: boolean; alertSchedule: string }>('/api/push/schedule', {
      method: 'PUT',
      body: JSON.stringify({ alertSchedule }),
    }),
};
